import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Pause, Play, SkipBack, SkipForward } from "lucide-react";
import { cn } from "../../lib/utils";

interface RejiTransportProps {
  paused: boolean;
  busy: boolean;
  disabled: boolean;
  onPrev: () => void;
  onToggle: () => void;
  onNext: () => void;
}

/** Big thumb-zone transport — every screen reacts together. */
function RejiTransportImpl({ paused, busy, disabled, onPrev, onToggle, onNext }: RejiTransportProps) {
  const { t } = useTranslation();
  const off = busy || disabled;
  const side =
    "flex items-center justify-center size-14 rounded-full border border-white/15 bg-white/5 text-white " +
    "hover:bg-white/15 active:scale-95 transition-all cursor-pointer " +
    "disabled:opacity-35 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60";

  return (
    <div className="flex items-center justify-center gap-6 lg:gap-8 py-3 lg:py-6">
      <button onClick={onPrev} disabled={off} aria-label={t("slideshowRemote.previous")} title={t("slideshowRemote.previous")} className={side}>
        <SkipBack className="h-5 w-5" />
      </button>
      <button
        onClick={onToggle}
        disabled={off}
        aria-label={paused ? t("slideshowRemote.resume") : t("slideshowRemote.pause")}
        title={paused ? t("slideshowRemote.resume") : t("slideshowRemote.pause")}
        className={cn(side, "size-16 bg-primary/15 border-primary/40 text-primary hover:bg-primary/25")}
      >
        {paused ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
      </button>
      <button onClick={onNext} disabled={off} aria-label={t("slideshowRemote.next")} title={t("slideshowRemote.next")} className={side}>
        <SkipForward className="h-5 w-5" />
      </button>
    </div>
  );
}

export const RejiTransport = memo(RejiTransportImpl);
